import type { TrialAccessRole, TrialAccessState } from "@/lib/trialAccess";
import { capturePostHogEvent } from "@/utils/posthog";
import { getSessionItem, setSessionItem } from "@/utils/safeStorage";

export type TrialAnalyticsEventName =
  | "trial_confirmation_shown"
  | "trial_confirmation_completed"
  | "trial_toolbar_shown"
  | "trial_toolbar_clicked"
  | "trial_pricing_opened"
  | "trial_plan_selected"
  | "checkout_started"
  | "checkout_returned"
  | "checkout_completed"
  | "trial_expired_viewed"
  | "locked_bot_viewed"
  | "locked_bot_pricing_clicked";

export interface TrialAnalyticsProperties {
  role: TrialAccessRole;
  trialState: TrialAccessState;
  daysRemaining?: number | null;
  surface: string;
  planId?: string;
}

export function captureTrialEvent(
  eventName: TrialAnalyticsEventName,
  properties: TrialAnalyticsProperties,
) {
  capturePostHogEvent(eventName, {
    role: properties.role,
    trial_state: properties.trialState,
    ...(typeof properties.daysRemaining === "number"
      ? { days_remaining: properties.daysRemaining }
      : {}),
    surface: properties.surface,
    ...(properties.planId ? { plan_id: properties.planId } : {}),
  });
}

const CHECKOUT_INTENT_KEY = "trial-checkout-analytics-intent-v1";

export type TrialCheckoutIntent = {
  id: string;
  role: TrialAccessRole;
  surface: string;
  planId?: string;
  purchaseType?: string;
  verificationSubjectId?: string;
};

export function markTrialCheckoutStarted({
  role,
  surface,
  planId,
  purchaseType,
  verificationSubjectId,
}: Omit<TrialCheckoutIntent, "id">) {
  const intent: TrialCheckoutIntent = {
    id: typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    role,
    surface,
    ...(planId ? { planId } : {}),
    ...(purchaseType ? { purchaseType } : {}),
    ...(verificationSubjectId ? { verificationSubjectId } : {}),
  };
  setSessionItem(CHECKOUT_INTENT_KEY, JSON.stringify(intent));
  capturePostHogEvent("checkout_started", {
    role,
    surface,
    ...(planId ? { plan_id: planId } : {}),
    ...(purchaseType ? { purchase_type: purchaseType } : {}),
  });
}

export function getTrialCheckoutIntent(): TrialCheckoutIntent | null {
  const storedIntent = getSessionItem(CHECKOUT_INTENT_KEY);
  if (!storedIntent) return null;
  try {
    return JSON.parse(storedIntent) as TrialCheckoutIntent;
  } catch {
    return null;
  }
}

export function captureTrialCheckoutReturn({
  role,
  checkoutStatus,
  paidAccessConfirmed,
}: {
  role: TrialAccessRole;
  checkoutStatus: "success" | "canceled";
  paidAccessConfirmed: boolean;
}) {
  const intent = getTrialCheckoutIntent();
  const intentId = intent?.id ?? `untracked-${role}-${checkoutStatus}`;
  const returnedKey = `${CHECKOUT_INTENT_KEY}:${intentId}:returned`;
  if (getSessionItem(returnedKey) !== "1") {
    capturePostHogEvent("checkout_returned", {
      role,
      checkout_status: checkoutStatus,
      surface: intent?.surface ?? "post_auth",
      ...(intent?.planId ? { plan_id: intent.planId } : {}),
      ...(intent?.purchaseType ? { purchase_type: intent.purchaseType } : {}),
    });
    setSessionItem(returnedKey, "1");
  }

  if (!paidAccessConfirmed) return;
  const completedKey = `${CHECKOUT_INTENT_KEY}:${intentId}:completed`;
  if (getSessionItem(completedKey) === "1") return;
  capturePostHogEvent("checkout_completed", {
    role,
    surface: intent?.surface ?? "post_auth",
    ...(intent?.planId ? { plan_id: intent.planId } : {}),
    ...(intent?.purchaseType ? { purchase_type: intent.purchaseType } : {}),
  });
  setSessionItem(completedKey, "1");
}
